import { parseSseEvents } from '../utils/sync/sseParser';
import { useSessionStore } from '../stores/session/sessionStore';
import { buildTenantHeaders } from '../utils/auth/tenantHeaders';
import { ensureFreshAppToken } from './appSession';
import { requestPortalRenonce } from '../utils/portal/portalRenonce';
import { ApiErrorCode } from './apiErrorCodes';
import { BASE_URL } from '../env';

export { BASE_URL };

async function getAuthHeader(): Promise<Record<string, string>> {
  // The app access token is the single API bearer for both portal and desktop sessions; it is minted
  // at login (portal-auth / app-session) and renewed via /connect/portal-refresh, so it outlives the
  // host IS JWT. The IS / OIDC token is only ever presented directly to the bootstrap endpoints
  // (sso/tenants, sso/login, app-session) — never here.
  const appToken = await ensureFreshAppToken();
  if (appToken) return { Authorization: `Bearer ${appToken}` };
  return {};
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly params?: Record<string, string>,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

interface ApiErrorPayload {
  code?: string;
  message?: string;
  title?: string;
  params?: Record<string, string>;
}

async function parseErrorResponse(response: Response, fallback: string): Promise<HttpError> {
  let errorMessage = fallback;
  let code: string | undefined;
  let params: Record<string, string> | undefined;
  try {
    const body = await response.json() as ApiErrorPayload | null;
    if (body && typeof body === 'object') {
      errorMessage = body.message ?? body.title ?? fallback;
      code = body.code;
      params = body.params;
    }
  } catch {
    // Response body is not JSON — keep the status fallback
  }
  // A cold server-side AcceoConnect session surfaces as this code. Only portal sessions recover via a
  // host re-nonce (the host has the Identity Server token we lack); desktop sessions hold a live OIDC
  // token and refresh AcceoConnect themselves, so they must not post a re-nonce request.
  if (code === ApiErrorCode.ConnectSessionExpired
    && useSessionStore.getState().sessionKind === 'portal') {
    requestPortalRenonce();
  }
  return new HttpError(response.status, errorMessage, code, params);
}

async function requestBlob(path: string): Promise<{ blob: Blob; contentType: string | null }> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      headers: { ...(await getAuthHeader()), ...(await buildTenantHeaders()) },
    });
  } catch (e) {
    throw new Error(`Network error on GET ${path}: ${(e as Error).message}`);
  }
  if (!response.ok) {
    // Parse the error body so connect-backed blob fetches (e.g. attachment preview) carry the
    // error code — lets a cold AcceoConnect session trigger the re-nonce flow.
    throw await parseErrorResponse(response, `${response.status}: ${response.statusText}`);
  }
  try {
    return { blob: await response.blob(), contentType: response.headers.get('content-type') };
  } catch (e) {
    throw new Error(`Failed to read response body on GET ${path}: ${(e as Error).message}`);
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeader()), ...(await buildTenantHeaders()), ...options?.headers },
      ...options,
    });
  } catch (e) {
    throw new Error(`Network error on ${options?.method ?? 'GET'} ${path}: ${(e as Error).message}`);
  }

  if (!response.ok) {
    throw await parseErrorResponse(response, `${response.status}: ${response.statusText}`);
  }

  if (!response.headers.get('content-type')?.includes('application/json')) return undefined as T;

  const data: unknown = await response.json();
  return data as T;
}

async function requestFormData<T>(path: string, body: FormData): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      // No Content-Type header — browser sets it with the multipart boundary automatically
      headers: { ...(await getAuthHeader()), ...(await buildTenantHeaders()) },
      body,
    });
  } catch (e) {
    throw new Error(`Network error on POST ${path}: ${(e as Error).message}`);
  }

  if (!response.ok) {
    throw await parseErrorResponse(response, `${response.status}: ${response.statusText}`);
  }

  if (!response.headers.get('content-type')?.includes('application/json')) return undefined as T;

  const data: unknown = await response.json();
  return data as T;
}

async function sseOnce<T>(path: string): Promise<T> {
  for await (const data of streamText(path)) {
    return JSON.parse(data) as T;
  }
  throw new Error(`SSE stream ended without a message on ${path}`);
}

async function* sseStream<T>(path: string, signal?: AbortSignal): AsyncGenerator<T> {
  for await (const data of streamText(path, signal)) {
    yield JSON.parse(data) as T;
  }
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path),
  getBlob: (path: string) => requestBlob(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  postFormData: <T>(path: string, body: FormData) => requestFormData<T>(path, body),
  remove: (path: string): Promise<void> => request<void>(path, { method: 'DELETE' }),
  sseOnce: <T>(path: string) => sseOnce<T>(path),
  sseStream: <T>(path: string, signal?: AbortSignal) => sseStream<T>(path, signal),
};

export async function* streamText(path: string, signal?: AbortSignal): AsyncGenerator<string> {
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: { Accept: 'text/event-stream', ...(await getAuthHeader()), ...(await buildTenantHeaders()) },
    signal,
  });
  if (!response.ok) throw new HttpError(response.status, response.statusText);

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const { events, remainder } = parseSseEvents(buffer);
      buffer = remainder;

      for (const data of events) {
        if (data !== '[DONE]') yield data;
      }
    }
  } finally {
    reader.releaseLock();
  }
}
